import { getRawInitData } from './telegram'
import type { StoredProfile } from './storage'
import { texts } from '@/texts'

const API_URL = (import.meta.env.VITE_PUSH_API_URL as string | undefined) ?? ''

export class PushApiError extends Error {
  constructor(message: string, public code: string = 'unknown') {
    super(message)
  }
}

function assertConfigured(): string {
  if (!API_URL) {
    throw new PushApiError(texts.errors.pushNotConfigured, 'not_configured')
  }
  return API_URL
}

function assertInitData(): string {
  const initData = getRawInitData()
  if (!initData) {
    throw new PushApiError(texts.errors.pushTelegramOnly, 'no_initdata')
  }
  return initData
}

async function parseOrThrow(res: Response): Promise<unknown> {
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    const code = typeof (data as { error?: string }).error === 'string' ? (data as { error: string }).error : `http_${res.status}`
    throw new PushApiError(`${texts.errors.apiErrorPrefix}${code}`, code)
  }
  return data
}

export interface RegisterPushInput {
  profile: StoredProfile
  preferredHourLocal: number // 0–23 in user's local time
}

export async function registerPush({ profile, preferredHourLocal }: RegisterPushInput) {
  const baseUrl = assertConfigured()
  const initData = assertInitData()

  // Date.getTimezoneOffset() returns minutes WEST of UTC (positive if behind UTC).
  // We want minutes EAST of UTC (positive if ahead), so we negate.
  const tzOffsetMin = -new Date().getTimezoneOffset()

  const res = await fetch(`${baseUrl}/api/register`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      initData,
      profile: { name: profile.name, dateOfBirth: profile.dateOfBirth },
      preferredHourLocal,
      tzOffsetMin,
    }),
  })

  return parseOrThrow(res) as Promise<{ ok: true; preferred_hour_utc: number }>
}

export async function unregisterPush() {
  const baseUrl = assertConfigured()
  const initData = assertInitData()

  const res = await fetch(`${baseUrl}/api/unregister`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ initData }),
  })

  return parseOrThrow(res) as Promise<{ ok: true }>
}

export async function syncAnitaUpdatesPreference(profile: StoredProfile, enabled: boolean) {
  const baseUrl = assertConfigured()
  const initData = assertInitData()

  const res = await fetch(`${baseUrl}/api/anita-updates`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      initData,
      enabled,
      profile: { name: profile.name },
    }),
  })

  return parseOrThrow(res) as Promise<{ ok: true; enabled: boolean }>
}

export async function getAnitaUpdatesStatus() {
  const baseUrl = assertConfigured()
  const initData = assertInitData()

  const res = await fetch(`${baseUrl}/api/anita-updates?initData=${encodeURIComponent(initData)}`)
  return parseOrThrow(res) as Promise<{ enabled: boolean }>
}

export function isPushAvailable(): boolean {
  return Boolean(API_URL) && Boolean(getRawInitData())
}
